import {
    Agent,
    AgentType,
    AgentValues,
    AIAssist2AgentValues,
    ClaimsMappingValue,
    CreateAgentRequest,
    EmailType,
    ExtractParametersType,
    JSAgentSettingsTypes,
    JSAgentValues,
    NightAgentValues,
    RestAgentValues,
    RoutingAgentValues,
    UpdateAgentRequest
} from "../../models/agent"
import { Time } from "../common/time"
import {
    OverrideChannelTypeParamType,
    OverrideChannelTypeValue
} from "../../components/AgentForm/agents/PsbBssAgent/OverrideChannelTypeControl"
import { ParamValues } from "../../models/parameterDeclaration"
import {
    JSAGENT_ADDITIONAL_SETTINGS_KEY,
    JSAGENT_AIASSIST_AGENT_ID_KEY,
    JSAGENT_API_VERSION_KEY,
    JSAGENT_CODE_KEY,
    JSAGENT_IS_MODULE_KEY,
    JSAGENT_MODULES_KEY,
    JSAGENT_TIMEOUT_KEY,
    SPACES_COUNT
} from "./javascriptAgentValues"

export const isNightAgentValues = (values: AgentValues): values is NightAgentValues => values.hasOwnProperty("Schedule")

const buildRedirectReplyWithIntentCommand = (indentId: string) =>
    indentId ? `/redirect aiassist2 intent_id="${indentId}"` : ""

const buildSwitchRedirectReplyWithIntentCommand = (indentId: string) =>
    indentId ? `/switchredirect aiassist2 intent_id="${indentId}"` : ""

const appendParams = (agentType: AgentType, params: ParamValues, values: AgentValues) => {
    switch (agentType) {
        case AgentType.InitialMsgAgent:
            params.text = buildSwitchRedirectReplyWithIntentCommand(values.Params.text)
            params.required_authorization = values.Params.required_authorization
            break
        case AgentType.ABAgent:
            params.probability = `${params.probability / 100}`
            break
        case AgentType.JqAgent:
            params["success-message"] = buildSwitchRedirectReplyWithIntentCommand(params["success-message"])

            params["failure-message"] = buildSwitchRedirectReplyWithIntentCommand(params["failure-message"])

            break
        case AgentType.EmailAgent:
            if (params["email-type"] === EmailType.Text) {
                params["intent-id"] = ""
            } else {
                params["body"] = ""
            }
            delete params["email-type"]

            if (!params["enable-auth"]) {
                params["smtp-login"] = ""
                params["smtp-password"] = ""
            }
            break
        case AgentType.NightAgent:
            const formatTime = (v: number) => v.toString().padStart(2, "0")

            if (isNightAgentValues(values)) {
                params.schedule = values.Schedule.map(s => ({
                    NightStartTime: `${formatTime(s.NightStartHr)}:${formatTime(s.NightStartMin)}`,
                    NightEndTime: `${formatTime(s.NightEndHr)}:${formatTime(s.NightEndMin)}`,
                    WeekDays: s.WeekDays.reduce((arr: number[], next, index) => {
                        next && arr.push(index + 1)
                        return arr
                    }, []),
                    Dates: s.Dates
                })).filter(s => s.WeekDays.length + s.Dates.length > 0)
            }
            break
        case AgentType.PsbBssAgent:
            params["continue-session-slot-ids"] = params["continue-session-slot-ids"].join(" ")

            const allowedExtensions = params["allowed-attachment-extensions"]
            params["allowed-attachment-extensions"] = allowedExtensions.length ? "." + allowedExtensions.join(" .") : ""

            params["override-channel-type"] = params["override-channel-type"].reduce(
                (obj: OverrideChannelTypeParamType, v: OverrideChannelTypeValue) => {
                    obj[v.from] = v.to
                    return obj
                },
                {}
            )

            break
        case AgentType.ADFSAgent:
            params["claims-mapping"] = params["claims-mapping"].map((v: ClaimsMappingValue) => ({
                claim: v.claim,
                slot: v.slot,
                field: v.fieldEnabled ? v.field : ""
            }))
            break
        case AgentType.FixedReplyAgent:
            params.text = buildRedirectReplyWithIntentCommand(params.text)
            break
    }

    Object.keys(params).forEach(key => {
        if (typeof params[key] !== "string") {
            params[key] = JSON.stringify(params[key])
        }
    })
    return params
}

const buildRoutingAgentRequest = (values: RoutingAgentValues) => ({
    "routing:cc": values.DefaultSettings.call_center,
    "routing:lang": values.DefaultSettings.lang,
    "routing:skill": values.DefaultSettings.skill,
    "routing:group": values.DefaultSettings.group,
    "routing:max_dialogs": values.DefaultSettings.max_dialogs.toString(),
    "routing:operator_id": values.DefaultSettings.operator_id,
    allow_only_known_agents: values.allow_only_known_agents.toString(),
    queue_id: values.DefaultSettings.queue_id,
    "routing:reply_after_routing_command": buildRedirectReplyWithIntentCommand(
        values.DefaultSettings.reply_after_routing
    )
})

export const buildCreateAgentRequest = (agentType: AgentType, values: AgentValues): CreateAgentRequest => {
    return {
        Type: agentType,
        ...(values.NextAgent && { NextAgent: values.NextAgent }),
        ...(values.FailOverAgent && { FailOverAgent: values.FailOverAgent }),
        Params: { ...appendParams(agentType, values.Params, values) }
    }
}

export const buildUpdateAgentRequest = (agentType: AgentType, values: AgentValues): UpdateAgentRequest => {
    return {
        ...(values.NextAgent && { NextAgent: values.NextAgent }),
        ...(values.FailOverAgent && { FailOverAgent: values.FailOverAgent }),
        Params: { ...appendParams(agentType, values.Params, values) }
    }
}

export const buildAIAssist2AgentParams = (values: AIAssist2AgentValues) => {
    const classifiersConf = values.ClassifiersConf.map(c => {
        const classifier = { ...c }
        classifier.BotClassificationResultThreshold /= 100
        return classifier
    })
    const { max_intent_repetition_enabled, ...params } = values.Params as ParamValues

    Object.keys(params).forEach(key => {
        if (typeof params[key] !== "string") {
            params[key] = JSON.stringify(params[key])
        }
    })

    return {
        ...params,
        stop_sf_intent: values.IntentSettings.stop_sf_intent,
        fail_intent: values.IntentSettings.fail_intent,
        file_input_intent: values.IntentSettings.file_input_intent,
        classifiers_conf: JSON.stringify(classifiersConf),
        max_intent_repetition: max_intent_repetition_enabled ? params.max_intent_repetition.toString() : "999",
        button_classifier_threshold: (values.Params.button_classifier_threshold / 100).toString()
    }
}

const buildRestAgentRequest = (values: RestAgentValues) => {
    const getRequest = (type: "Requests" | "AuthorizationRequest") => {
        const param = values[type]
        return {
            BaseUri: param.BaseUri,
            Method: param.Method,
            Query: param.Query,
            Headers: param.Headers,
            Body: param.BodyEnabled || type === "AuthorizationRequest" ? param.Body : "",
            ...(type === "Requests" && {
                TimeoutSec: param.TimeoutSecEnabled ? param.TimeoutSec : ""
            }),
            ResponseParameters: {
                HttpCodeToSlot: param.HttpCodeToSlot,
                ExtractParameters: param.ExtractParameters.map(v => ({
                    ...(v.Type === ExtractParametersType.Header && {
                        Header: v.Header
                    }),
                    ...(v.Type === ExtractParametersType.JsonPaths &&
                        !v.WholeResponse && {
                            JsonPaths: v.JsonPaths
                        }),
                    SlotId: v.SlotId
                }))
            }
        }
    }

    const requests = getRequest("Requests")

    return {
        requests: JSON.stringify(
            values.RequestTokenEnabled
                ? { Requests: [requests], AuthorizationRequest: getRequest("AuthorizationRequest") }
                : { Requests: [requests] }
        ),
        "custom-api-key": values.CustomApiKeyEnabled ? values.Params["custom-api-key"] : "",
        "success-message": values.Params["success-message"],
        "failure-message": values.Params["failure-message"]
    }
}

const buildJSAgentRequest = (values: JSAgentValues, agentApiVersion: string) => {
    const settings = JSON.parse(values.Settings) ?? {}

    for (const declaration of values.Declaration) {
        settings[declaration.id] =
            declaration.type === JSAgentSettingsTypes.Object
                ? JSON.parse(declaration.value as string)
                : declaration.value
    }

    return {
        [JSAGENT_ADDITIONAL_SETTINGS_KEY]: JSON.stringify(settings, undefined, SPACES_COUNT),
        [JSAGENT_TIMEOUT_KEY]: Time.secondsToMs(values.Timeout).toString(),
        [JSAGENT_CODE_KEY]: values.Code,
        [JSAGENT_AIASSIST_AGENT_ID_KEY]: values.AIAgent,
        [JSAGENT_API_VERSION_KEY]: agentApiVersion,
        [JSAGENT_IS_MODULE_KEY]: String(values.IsModule),
        [JSAGENT_MODULES_KEY]: values.Modules.join(", ")
    }
}

export const buildCreateJSAgentRequest = (values: JSAgentValues): CreateAgentRequest => {
    const params = buildJSAgentRequest(values, "")

    return {
        Type: AgentType.JSAgent,
        FailOverAgent: values.FailOverAgent,
        Params: params
    }
}

export const buildUpdateJSAgentRequest = (values: JSAgentValues, agentApiVersion: string): UpdateAgentRequest => {
    const params = buildJSAgentRequest(values, agentApiVersion)

    return {
        FailOverAgent: values.FailOverAgent,
        Params: params
    }
}

export const buildAIAssist2AgentUpdateRequest = (values: AIAssist2AgentValues): UpdateAgentRequest => {
    return {
        Params: buildAIAssist2AgentParams(values)
    }
}

export const buildAIAssist2AgentCreateRequest = (values: AIAssist2AgentValues): CreateAgentRequest => {
    return {
        Type: AgentType.AIAssist2,
        Params: buildAIAssist2AgentParams(values)
    }
}

export const buildUpdateRestAgentRequest = (values: RestAgentValues): UpdateAgentRequest => {
    const params = buildRestAgentRequest(values)

    return {
        NextAgent: values.NextAgent,
        Params: params
    }
}

export const buildCreateRestAgentRequest = (values: RestAgentValues): CreateAgentRequest => {
    const params = buildRestAgentRequest(values)

    return {
        Type: AgentType.RestAgent,
        NextAgent: values.NextAgent,
        Params: params
    }
}

export const buildUpdateRoutingAgentRequest = (values: RoutingAgentValues): UpdateAgentRequest => {
    return {
        FailOverAgent: values.FailOverAgent,
        Params: buildRoutingAgentRequest(values)
    }
}

export const buildCreateRoutingAgentRequest = (values: RoutingAgentValues): CreateAgentRequest => {
    return {
        Type: AgentType.RoutingAgent,
        FailOverAgent: values.FailOverAgent,
        Params: buildRoutingAgentRequest(values)
    }
}
